import { yupResolver } from '@hookform/resolvers/yup';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormHelperText,
    Grid,
    MenuItem
} from '@mui/material';
import { addDays, startOfDay } from 'date-fns';
import { isBefore } from 'date-fns/esm';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { connect, useDispatch } from 'react-redux';
import * as yup from 'yup';
import { FormDatePicker, FormSelect, FormTextField } from '../../../common/form';
import { ProgressButton } from '../../../common/ProgressButton';
import { RootState } from '../../../store';
import CustomerTree from '../../customer/CustomerTree';
import { getCurrentUser } from '../../user/redux/userSelectors';
import { getAppSettings, getSettingsUi } from '../redux/settingsSelectors';
import { sendPricelistsToEmail } from '../redux/settingsSlices';

const mapStateToProps = (state: RootState) => ({
    isSending: getSettingsUi(state).isSendingPricelistMails,
    currentUserEmail: getCurrentUser(state)?.username,
    mailSendouts: getAppSettings(state).mailSendouts
});

type OwnProps = {
    closeDialog(): void;
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

type SendPricelistMailsForm = {
    mailSendoutId: string;
    customerIds: number[];
    validAt?: Date;
    email: string;
};

const schema: yup.SchemaOf<SendPricelistMailsForm> = yup.object().shape({
    mailSendoutId: yup.string().required().label('Mail sendout'),
    customerIds: yup
        .array()
        .min(1, 'At least one customer must be selected')
        .of(yup.number().required())
        .label('Customers'),
    validAt: yup
        .date()
        .required()
        .min(startOfDay(addDays(new Date(), 1)), "'Valid at' must be a future date")
        .label('Valid at'),
    email: yup.string().email().required().label('Email')
});

const SendPricelistsToMailDialog = ({ isSending, closeDialog, currentUserEmail, mailSendouts }: Props) => {
    const dispatch = useDispatch();

    const minDate = useMemo(() => startOfDay(addDays(new Date(), 1)), []);

    const { control, handleSubmit } = useForm<SendPricelistMailsForm>({
        defaultValues: useMemo(
            () => ({
                customerIds: [],
                validAt: minDate,
                email: currentUserEmail
            }),
            [minDate, currentUserEmail]
        ),
        resolver: yupResolver(schema)
    });

    const handleFormSubmit = (form: SendPricelistMailsForm) => {
        dispatch(
            sendPricelistsToEmail.request({
                mailSendoutId: form.mailSendoutId,
                customerIds: form.customerIds,
                validAt: form.validAt!.toISOString(),
                email: form.email
            })
        );
    };

    const shouldDisableDate = (date: Date | null) => {
        if (date == null) {
            return false;
        }

        return isBefore(date, minDate);
    };

    return (
        <Dialog open={true}>
            <DialogTitle>Send customer pricelists to email</DialogTitle>
            <DialogContent sx={{ height: 600, width: 500 }}>
                <form id="sendPricelistMailsToUserDialog" onSubmit={handleSubmit(handleFormSubmit)}>
                    <Grid container direction="column" spacing={4}>
                        <Grid item xs={6}>
                            <FormSelect label="Mail sendout" name="mailSendoutId" control={control}>
                                {mailSendouts.map(s => (
                                    <MenuItem key={s.mailSendoutId} value={s.mailSendoutId}>
                                        {s.name}
                                    </MenuItem>
                                ))}
                            </FormSelect>
                        </Grid>
                        <Grid item xs={8}>
                            <FormTextField control={control} name="email" label="Email" fullWidth />
                        </Grid>
                        <Grid item xs={6}>
                            <FormDatePicker
                                name="validAt"
                                label="Valid at"
                                control={control}
                                inputFormat="yyyy-MM-dd"
                                disablePast={true}
                                shouldDisableDate={shouldDisableDate}
                            />
                        </Grid>
                        <Grid item>
                            <Controller
                                control={control}
                                name="customerIds"
                                render={({ field: { onChange, value }, fieldState: { error } }) => (
                                    <>
                                        <CustomerTree
                                            multiSelect
                                            selectedCustomerIds={value}
                                            setSelectedCustomerIds={onChange}
                                        />
                                        {error != null && (
                                            <FormHelperText error={true}>{error.message}</FormHelperText>
                                        )}
                                    </>
                                )}
                            />
                        </Grid>
                    </Grid>
                </form>
            </DialogContent>
            <DialogActions>
                <Button onClick={closeDialog}>Cancel</Button>
                <ProgressButton
                    color="primary"
                    type="submit"
                    form="sendPricelistMailsToUserDialog"
                    isLoading={isSending}
                >
                    Send
                </ProgressButton>
            </DialogActions>
        </Dialog>
    );
};

export default connect(mapStateToProps)(SendPricelistsToMailDialog);
